import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import CandidateCompleteMilestoneForm from "@/components/forms/opportunity/milestone-candidate-complete";
import CompanyCompleteMilestoneForm from "@/components/forms/opportunity/milestone-company-confirm-and-pay";
import PayMilestonesForm from "@/components/forms/opportunity/milestone-company-pay";

import { CandidateStatus, MinimalCandidateStatus } from "@/types";

function formatIsoDateForLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

const ViewMilestonesCandidate = ({
  jobOpeningId,
  candidate,
}: {
  jobOpeningId: string;
  candidate: CandidateStatus | MinimalCandidateStatus;
}) => {
  const [open, setOpen] = useState(false);

  const { billing } = candidate;

  if (!billing) {
    return;
  }

  // If no milestones, return
  const milestones = billing.terms.filter(
    (term) => term.termType === "milestone",
  );

  if (!milestones.length) {
    return;
  }

  const completedMilestones = milestones.filter(
    (term) => term.status !== "in_progress",
  );
  const unfinishedMilestones = milestones.filter(
    (term) => term.status === "in_progress",
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={unfinishedMilestones.length ? "secondary" : "outline"}>
          {unfinishedMilestones.length ? "Update" : "Review"} Milestones
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>
          {unfinishedMilestones.length ? "Update" : "Review"} Milestones
        </DialogTitle>
        {completedMilestones.length ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Completed Milestone(s)
            </h3>
            <div className="space-y-2">
              {completedMilestones.map((milestone) => (
                <div
                  key={`milestone-${milestone.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {milestone.milestoneLabel}
                  </span>
                  <span className="text-sm text-gray-500">
                    {milestone.submittedAt
                      ? formatIsoDateForLabel(milestone.submittedAt)
                      : undefined}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {unfinishedMilestones.length ? (
          <CandidateCompleteMilestoneForm
            jobOpeningId={jobOpeningId}
            milestoneOptions={unfinishedMilestones}
            onCancel={() => setOpen(false)}
            onSuccess={() => setOpen(false)}
          />
        ) : (
          <>
            <div className="h-[1px] bg-[#E4E7EC] w-full" />
            <div className="!mb-1 flex">
              <Button
                variant="stroke"
                className="mt-2"
                onClick={() => setOpen(false)}
              >
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ViewMilestonesCompany = ({
  jobOpeningId,
  candidate,
}: {
  jobOpeningId: string;
  candidate: CandidateStatus | MinimalCandidateStatus;
}) => {
  const [open, setOpen] = useState(false);

  const { billing } = candidate;

  if (!billing) {
    return;
  }

  // If no milestones, return
  const milestones = billing.terms.filter(
    (term) => term.termType === "milestone",
  );

  if (!milestones.length) {
    return;
  }

  const unpaidMilestones = milestones.filter(
    (term) => term.status === "submitted",
  );
  const unfinishedMilestones = milestones.filter(
    (term) => term.status === "in_progress",
  );
  const completedMilestones = milestones.filter(
    (term) => term.status === "paid",
  );

  const milestonesRequirePayment = Boolean(unpaidMilestones.length);
  const milestonesAreIncomplete = Boolean(unfinishedMilestones.length);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={
            milestonesRequirePayment || milestonesAreIncomplete
              ? "secondary"
              : "outline"
          }
        >
          {milestonesRequirePayment
            ? "Confirm"
            : milestonesAreIncomplete
              ? "Update"
              : "Review"}{" "}
          Milestones
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>
          {milestonesRequirePayment
            ? "Confirm"
            : milestonesAreIncomplete
              ? "Update"
              : "Review"}{" "}
          Milestones
        </DialogTitle>
        {completedMilestones.length ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Completed Milestone(s)
            </h3>
            <div className="space-y-2">
              {completedMilestones.map((milestone) => (
                <div
                  key={`milestone-${milestone.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {milestone.milestoneLabel}
                  </span>
                  <span className="text-sm text-gray-500">
                    {milestone.submittedAt
                      ? formatIsoDateForLabel(milestone.submittedAt)
                      : undefined}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {unpaidMilestones.length ? (
          <PayMilestonesForm
            milestoneOptions={unpaidMilestones}
            onCancel={() => setOpen(false)}
          />
        ) : unfinishedMilestones.length ? (
          <CompanyCompleteMilestoneForm
            jobOpeningId={jobOpeningId}
            candidateStatusId={candidate.id}
            milestoneOptions={unfinishedMilestones}
            onCancel={() => setOpen(false)}
            onSuccess={() => setOpen(false)}
          />
        ) : (
          <>
            <div className="h-[1px] bg-[#E4E7EC] w-full" />
            <div className="!mb-1 flex">
              <Button
                variant="stroke"
                className="mt-2"
                onClick={() => setOpen(false)}
              >
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { ViewMilestonesCandidate, ViewMilestonesCompany };
